import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Alert,
  Button,
  Checkbox,
  Input,
  Select,
  SelectItem,
  DateField,
} from '@structural-climbing/ui'
import type { GymPlan, Subscription } from '../plans/types'
import { PlanPriceFields } from '../plans/PlanPriceFields'
import { usePlanDiscount } from '../plans/usePlanDiscount'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { useGymTimezone } from '../../contexts/GymContext'
import { gymLocalDate } from '../../lib/gymLocalDate'
import {
  formatDay,
  previewRenewalMove,
  type RenewalMovePreview,
  type UpcomingPlan,
} from './renewalShift'
import { GymDialog } from '../../components/GymDialog'

/** What the server reports after moving the renewal: the dates it really set. */
interface RenewalMoved {
  plan_name: string | null
  to: { starts_at: string; expires_at: string | null }
}

interface BillingForm {
  billingType: 'individual' | 'company'
  firstName: string
  lastName: string
  cnp: string
  companyName: string
  cui: string
  regCom: string
  address: string
  city: string
  county: string
}

const emptyBilling = (): BillingForm => ({
  billingType: 'individual',
  firstName: '',
  lastName: '',
  cnp: '',
  companyName: '',
  cui: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
})

interface EditPlanModalProps {
  subscription: Subscription
  memberEmail: string
  plans: GymPlan[]
  /** A renewal queued behind this plan. Editing the end date moves it, and the
   *  desk is shown where it goes before saving. */
  upcoming?: UpcomingPlan | null
  onUpdated: () => void
  onDeleted: () => void
  onClose: () => void
}

export function EditPlanModal({
  subscription,
  memberEmail,
  plans,
  upcoming = null,
  onUpdated,
  onDeleted,
  onClose,
}: EditPlanModalProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  // Include the current plan even if it's inactive so the form always has a valid selection
  // Same kind only: an add-on cannot become the main plan or the other way
  // round, and the API refuses the swap.
  const editablePlans = plans.filter(
    (p) =>
      (p.is_active || p.id === subscription.gym_plan_id) &&
      !!p.is_addon === !!subscription.is_addon,
  )

  const [view, setView] = useState<'edit' | 'confirmDelete'>('edit')
  const [selectedPlanId, setSelectedPlanId] = useState(subscription.gym_plan_id)
  const [pricePaid, setPricePaid] = useState(subscription.price_paid.toFixed(2))
  const timezone = useGymTimezone()
  // The gym's calendar day, not the UTC one: a plan starting at midnight in
  // Bucharest is stored as 21:00 or 22:00 UTC the day before.
  const initialStartsAt = gymLocalDate(new Date(subscription.starts_at), timezone)
  const [startsAt, setStartsAt] = useState(initialStartsAt)
  const [expiresAt, setExpiresAt] = useState(
    subscription.expires_at ? gymLocalDate(new Date(subscription.expires_at), timezone) : '',
  )
  const [entriesRemaining, setEntriesRemaining] = useState(
    subscription.entries_remaining?.toString() ?? '',
  )
  const [deleteInvoice, setDeleteInvoice] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<DeskPaymentMethod>('cash')
  const [issueInvoice, setIssueInvoice] = useState(false)
  const [billing, setBilling] = useState<BillingForm>(emptyBilling())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedPlan = plans.find((p) => p.id === selectedPlanId)
  const isEntryBased = selectedPlan?.plan_type === 'entry_based'

  // Where the queued renewal lands if this is saved. No entries left ends the
  // plan the same way a cancel does: the renewal starts tomorrow.
  const usedUp = isEntryBased && entriesRemaining !== '' && (parseInt(entriesRemaining) || 0) <= 0
  const editMove =
    upcoming && (usedUp || expiresAt)
      ? previewRenewalMove(upcoming, usedUp ? 'ended' : expiresAt, timezone)
      : null
  const cancelMove = upcoming ? previewRenewalMove(upcoming, 'ended', timezone) : null
  // This row is itself a renewal that has not started: cancelling it leaves the
  // member's current plan, and their check-in, untouched.
  const isQueuedRenewal =
    !!subscription.renews_subscription_id && new Date(subscription.starts_at) > new Date()

  function announceMove(moved: RenewalMoved | null | undefined) {
    if (!moved) return
    const day = (iso: string) => formatDay(gymLocalDate(new Date(iso), timezone), i18n.language)
    const plan = moved.plan_name ?? t('members.nextPlan')
    addToast({
      title: t('members.renewalMovedToast'),
      description: moved.to.expires_at
        ? t('members.renewalMovedToastBody', {
            plan,
            start: day(moved.to.starts_at),
            end: day(moved.to.expires_at),
          })
        : t('members.renewalMovedToastBodyOpen', { plan, start: day(moved.to.starts_at) }),
      color: 'success',
    })
  }
  // The percentage always comes off the list price. Taking it off again goes
  // back to what the member paid, unless the plan itself was changed.
  const discount = usePlanDiscount({
    selectedPlan,
    setPricePaid,
    restorePrice: (plan) =>
      plan.id === subscription.gym_plan_id ? subscription.price_paid : plan.price,
  })

  function handlePlanSelect(keys: Iterable<React.Key>) {
    const id = String(Array.from(keys)[0] ?? '')
    setSelectedPlanId(id)
    const plan = plans.find((p) => p.id === id)
    if (!plan) return
    setPricePaid(discount.priceFor(plan).toFixed(2))
    const start = new Date(startsAt || new Date().toISOString().slice(0, 10))
    if (!isNaN(start.getTime())) {
      const exp = new Date(start)
      exp.setDate(exp.getDate() + plan.duration_days - 1)
      setExpiresAt(exp.toISOString().slice(0, 10))
    }
    if (plan.plan_type === 'entry_based') {
      const originalPlan = plans.find((p) => p.id === subscription.gym_plan_id)
      const entriesUsed =
        originalPlan?.plan_type === 'entry_based'
          ? Math.max(0, (originalPlan.max_entries ?? 0) - (subscription.entries_remaining ?? 0))
          : 0
      setEntriesRemaining(String(Math.max(0, (plan.max_entries ?? 0) - entriesUsed)))
    } else {
      setEntriesRemaining('')
    }
    setError(null)
  }

  function handleStartsAtChange(val: string) {
    setStartsAt(val)
    if (selectedPlan && val) {
      const start = new Date(val)
      if (!isNaN(start.getTime())) {
        const exp = new Date(start)
        exp.setDate(exp.getDate() + selectedPlan.duration_days - 1)
        setExpiresAt(exp.toISOString().slice(0, 10))
      }
    }
  }

  function setBillingField(field: keyof BillingForm, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()

      const body: Record<string, unknown> = {
        gym_plan_id: selectedPlanId,
        price_paid: parseFloat(pricePaid) || 0,
        // Only a day the owner changed. The API turns it into midnight at the
        // gym; leaving an unchanged one out keeps the exact moment of the sale.
        ...(startsAt && startsAt !== initialStartsAt ? { starts_at: startsAt } : {}),
        expires_at: expiresAt || null,
        entries_remaining: isEntryBased ? parseInt(entriesRemaining) || 0 : null,
        status: 'active',
      }

      const patchRes = await fetch(`${apiUrl}/subscriptions/${subscription.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const patchJson = (await patchRes.json()) as {
        error?: string
        renewal_moved?: RenewalMoved | null
      }
      if (!patchRes.ok) {
        setError(patchJson.error ?? t('members.editPlanError'))
        return
      }
      announceMove(patchJson.renewal_moved)

      if (issueInvoice) {
        // Full replace: delete old order and create new one with billing details
        await fetch(`${apiUrl}/invoices/by-subscription/${subscription.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })

        const invoiceBody: Record<string, unknown> = {
          subscription_id: subscription.id,
          member_email: memberEmail,
          // A member added at the desk may have no email to be found by.
          member_user_id: subscription.user_id,
          payment_method: paymentMethod,
          total_amount: parseFloat(pricePaid) || 0,
          plan_name: selectedPlan?.name ?? '',
        }
        if (billing.billingType) {
          invoiceBody.billing = {
            billing_type: billing.billingType,
            first_name: billing.firstName || undefined,
            last_name: billing.lastName || undefined,
            cnp: billing.cnp || undefined,
            company_name: billing.companyName || undefined,
            cui: billing.cui || undefined,
            reg_com: billing.regCom || undefined,
            address: billing.address || undefined,
            city: billing.city || undefined,
            county: billing.county || undefined,
          }
        }
        const invRes = await fetch(`${apiUrl}/invoices`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(invoiceBody),
        })
        if (!invRes.ok) {
          setError(t('assignPlan.errorCreateInvoice'))
          onUpdated()
          return
        }
      } else {
        const syncRes = await fetch(`${apiUrl}/invoices/by-subscription/${subscription.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            total_amount: parseFloat(pricePaid) || 0,
            payment_method: paymentMethod,
            plan_name: selectedPlan?.name ?? '',
          }),
        })
        if (!syncRes.ok) console.warn('[EditPlanModal] Failed to sync order')
      }

      onUpdated()
    } catch {
      setError(t('members.editPlanError'))
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete() {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/subscriptions/${subscription.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ delete_invoice: deleteInvoice }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        error?: string
        renewal_moved?: RenewalMoved | null
      }
      if (!res.ok) {
        setError(data.error ?? t('members.deletePlanError'))
        setView('edit')
        return
      }
      announceMove(data.renewal_moved)
      onDeleted()
    } catch {
      setError(t('members.deletePlanError'))
      setView('edit')
    } finally {
      setLoading(false)
    }
  }

  return (
    <GymDialog onClose={onClose}>
      <div className="overflow-y-auto p-6">
        {view === 'confirmDelete' ? (
          <>
            <div className="mb-5">
              <h2 className="text-base font-semibold">
                {isQueuedRenewal
                  ? t('members.deleteRenewalConfirmTitle')
                  : t('members.deletePlanConfirmTitle')}
              </h2>
              <p className="mt-1 text-sm text-foreground/60">
                {isQueuedRenewal
                  ? t('members.deleteRenewalConfirmBody', {
                      date: formatDay(
                        gymLocalDate(new Date(subscription.starts_at), timezone),
                        i18n.language,
                      ),
                    })
                  : cancelMove
                    ? t('members.deletePlanConfirmBodyWithNext')
                    : t('members.deletePlanConfirmBody')}
              </p>
            </div>
            {upcoming && cancelMove && (
              <RenewalMoveNotice
                className="mb-4"
                planName={upcoming.plan_name ?? t('members.nextPlan')}
                move={cancelMove}
              />
            )}
            <div className="mb-4">
              <Checkbox isSelected={deleteInvoice} onValueChange={setDeleteInvoice}>
                {t('members.deleteInvoice')}
              </Checkbox>
            </div>
            {error && <p className="mb-3 text-sm text-danger">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="flat"
                isDisabled={loading}
                onClick={() => {
                  setError(null)
                  setView('edit')
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                color="danger"
                isLoading={loading}
                onClick={() => void handleDelete()}
              >
                {t('members.deletePlanConfirm')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-5">
              <h2 className="text-base font-semibold">{t('members.editPlanModalTitle')}</h2>
            </div>

            <div className="flex flex-col gap-4">
              <Select
                label={t('assignPlan.planLabel')}
                selectedKeys={selectedPlanId ? [selectedPlanId] : []}
                onSelectionChange={handlePlanSelect}
              >
                {editablePlans.map((p) => (
                  <SelectItem
                    key={p.id}
                    textValue={`${p.name} — ${p.price.toFixed(2)} ${p.currency}`}
                  >
                    {p.name} — {p.price.toFixed(2)} {p.currency}
                  </SelectItem>
                ))}
              </Select>

              {selectedPlan && (
                <PlanPriceFields
                  plan={selectedPlan}
                  pricePaid={pricePaid}
                  onPricePaidChange={setPricePaid}
                  discount={discount}
                  showListPriceHint={false}
                />
              )}

              <DateField
                label={t('assignPlan.transferStartDateLabel')}
                value={startsAt}
                onValueChange={handleStartsAtChange}
              />

              <DateField
                label={t('assignPlan.expiresAtLabel', { defaultValue: 'Expires at' })}
                value={expiresAt}
                onValueChange={setExpiresAt}
              />

              {isEntryBased && (
                <Input
                  label={t('assignPlan.entriesRemainingLabel', {
                    defaultValue: 'Entries remaining',
                  })}
                  type="number"
                  min="0"
                  value={entriesRemaining}
                  onValueChange={setEntriesRemaining}
                />
              )}

              {upcoming && editMove && (
                <RenewalMoveNotice
                  planName={upcoming.plan_name ?? t('members.nextPlan')}
                  move={editMove}
                />
              )}
              {/* Before anything changes: a quiet note that the end date is
                  tied to the next plan, so the move is never a surprise. */}
              {upcoming && !editMove && new Date(upcoming.starts_at) > new Date() && (
                <Alert
                  color="default"
                  variant="flat"
                  description={t('members.renewalMoveHint', {
                    plan: upcoming.plan_name ?? t('members.nextPlan'),
                    start: formatDay(
                      gymLocalDate(new Date(upcoming.starts_at), timezone),
                      i18n.language,
                    ),
                  })}
                />
              )}

              <Select
                label={t('assignPlan.paymentMethodLabel')}
                selectedKeys={[paymentMethod]}
                onSelectionChange={(keys) => {
                  const val = String(Array.from(keys)[0] ?? 'cash') as DeskPaymentMethod
                  setPaymentMethod(val)
                }}
              >
                <SelectItem key="cash">{t('assignPlan.cash')}</SelectItem>
                <SelectItem key="card">{t('assignPlan.card')}</SelectItem>
                <SelectItem key="transfer">{t('assignPlan.transfer')}</SelectItem>
              </Select>

              <Checkbox isSelected={issueInvoice} onValueChange={setIssueInvoice}>
                {t('assignPlan.issueInvoice')}
              </Checkbox>

              {issueInvoice && (
                <div className="flex flex-col gap-3 rounded-lg border border-divider p-4">
                  <Select
                    label={t('assignPlan.billingTypeLabel')}
                    selectedKeys={[billing.billingType]}
                    onSelectionChange={(keys) => {
                      const val = String(Array.from(keys)[0] ?? 'individual') as
                        | 'individual'
                        | 'company'
                      setBilling((prev) => ({ ...prev, billingType: val }))
                    }}
                  >
                    <SelectItem key="individual">{t('assignPlan.individual')}</SelectItem>
                    <SelectItem key="company">{t('assignPlan.company')}</SelectItem>
                  </Select>

                  {billing.billingType === 'individual' ? (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label={t('assignPlan.firstName')}
                          value={billing.firstName}
                          onValueChange={(v) => setBillingField('firstName', v)}
                        />
                        <Input
                          label={t('assignPlan.lastName')}
                          value={billing.lastName}
                          onValueChange={(v) => setBillingField('lastName', v)}
                        />
                      </div>
                      <Input
                        label={t('assignPlan.cnp')}
                        value={billing.cnp}
                        onValueChange={(v) => setBillingField('cnp', v)}
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        label={t('assignPlan.companyName')}
                        value={billing.companyName}
                        onValueChange={(v) => setBillingField('companyName', v)}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label={t('assignPlan.cui')}
                          value={billing.cui}
                          onValueChange={(v) => setBillingField('cui', v)}
                        />
                        <Input
                          label={t('assignPlan.regCom')}
                          value={billing.regCom}
                          onValueChange={(v) => setBillingField('regCom', v)}
                        />
                      </div>
                    </>
                  )}

                  <Input
                    label={t('assignPlan.address')}
                    value={billing.address}
                    onValueChange={(v) => setBillingField('address', v)}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label={t('assignPlan.city')}
                      value={billing.city}
                      onValueChange={(v) => setBillingField('city', v)}
                    />
                    <Input
                      label={t('assignPlan.county')}
                      value={billing.county}
                      onValueChange={(v) => setBillingField('county', v)}
                    />
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-danger">{error}</p>}

              <div className="flex justify-between gap-2">
                <Button
                  size="sm"
                  color="danger"
                  variant="flat"
                  isDisabled={loading}
                  onClick={() => setView('confirmDelete')}
                >
                  {t('members.deletePlanButton')}
                </Button>
                <div className="flex gap-2">
                  <Button size="sm" variant="flat" onClick={onClose} isDisabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    isLoading={loading}
                    isDisabled={!selectedPlanId || loading}
                    onClick={() => void handleSave()}
                  >
                    {t('members.editPlanSave')}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </GymDialog>
  )
}

/** The next plan's new dates, with the old ones struck through. */
function RenewalMoveNotice({
  planName,
  move,
  className,
}: {
  planName: string
  move: RenewalMovePreview
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const day = (d: string) => formatDay(d, i18n.language)
  return (
    <Alert
      className={className}
      // Grey, not the brand colour: in some gyms' themes that is red, and a
      // moved date is news, not an error. Yellow only when today is left bare.
      color={move.gapToday ? 'warning' : 'default'}
      variant="flat"
      title={t('members.renewalMoveTitle')}
      description={
        <div className="flex flex-col gap-1">
          <span>
            {move.to.end
              ? t('members.renewalMoveBody', {
                  plan: planName,
                  start: day(move.to.start),
                  end: day(move.to.end),
                })
              : t('members.renewalMoveBodyOpen', {
                  plan: planName,
                  start: day(move.to.start),
                })}
          </span>
          {move.from.end && (
            <span className="text-foreground/50 line-through">
              {t('members.renewalMoveWas', {
                start: day(move.from.start),
                end: day(move.from.end),
              })}
            </span>
          )}
          {move.gapToday && <span className="font-medium">{t('members.renewalMoveGap')}</span>}
        </div>
      }
    />
  )
}
